import type { BraceletPatternDto, EntityId, HexColor, PatternType } from '@miyuki/types';

import { isDomainError, type DomainError } from '../errors';

import { clearCell, paintCell } from './pattern.cells';
import {
  addColor,
  addColorRaisingDeclaredCount,
  raiseDeclaredColorCount,
  removeColor,
  updateColor,
} from './pattern.colors';
import { resizeColumns } from './pattern.columns';
import { applyPatch, isNoChange, type PatternEdit, type PatternPatch } from './pattern.entity';
import {
  clampProgress,
  completeRow,
  goToRow,
  resetProgress,
  setReadingAlternates,
} from './pattern.progress';
import { appendRow, duplicateRow, insertRowAbove, removeRow } from './pattern.rows';
import { applyDesignSettings } from './pattern.settings';
import { paintCells, type CellRef } from './pattern.stroke';
import { setPatternType } from './pattern.type';

/** Lo que un toque sobre el lienzo hace: pintar de un color, o vaciar. */
export type PatternTool =
  { readonly kind: 'color'; readonly index: number } | { readonly kind: 'eraser' };

export interface PatternSession {
  readonly pattern: BraceletPatternDto;
  /** Parches que deshacen, del más antiguo al más reciente. */
  readonly past: readonly PatternPatch[];
  /** Parches que rehacen. Cualquier cambio nuevo lo vacía. */
  readonly future: readonly PatternPatch[];
  readonly tool: PatternTool;
  /** Id del diseño guardado del que salió, o `null` si nunca se guardó. */
  readonly savedFrom: EntityId | null;
  /** Hay cambios que no están en el almacenamiento. */
  readonly dirty: boolean;
  /**
   * Error de la última acción, o `null`. El reductor nunca lanza: una acción
   * imposible —agregar la fila 501, un color por encima del tope— es algo que
   * la interfaz debe contar, no una excepción que reviente el árbol de React.
   */
  readonly lastError: DomainError | null;
  /**
   * Trazo en curso. `open` dice que hay uno; `committed`, que ya empujó su
   * propio parche al historial.
   *
   * Hacen falta los dos: un trazo puede abrirse sobre celdas que no cambian
   * nada —repasar con el color que ya tenían— y entonces todavía no tiene
   * parche. Fusionar en `past.at(-1)` sin `committed` reescribiría la acción
   * anterior, que es de otra cosa.
   */
  readonly stroke: StrokeState;
  /**
   * Avances anteriores, del más antiguo al más reciente, y los que quedan por
   * rehacer. Es un historial **aparte** del de edición: deshacer una fila
   * tejida no puede revertir una corrección del diseño ni al revés (RF-47).
   *
   * Guarda el valor **anterior** del avance, no un delta. Así un salto de
   * veinte filas ocupa una sola entrada, igual que una fila completada, y
   * deshacerlo vuelve al valor previo de una vez y no fila por fila (RF-44a,
   * RF-51c).
   */
  readonly progressPast: readonly number[];
  readonly progressFuture: readonly number[];
}

export interface StrokeState {
  readonly open: boolean;
  readonly committed: boolean;
}

const NO_STROKE: StrokeState = { open: false, committed: false };

export type PatternAction =
  | { readonly type: 'paint'; readonly row: number; readonly column: number }
  | { readonly type: 'selectTool'; readonly tool: PatternTool }
  | { readonly type: 'appendRow' }
  | { readonly type: 'insertRowAbove'; readonly row: number }
  | { readonly type: 'duplicateRow'; readonly row: number }
  | { readonly type: 'removeRow'; readonly row: number }
  | { readonly type: 'resizeColumns'; readonly columns: number }
  | { readonly type: 'setPatternType'; readonly patternType: PatternType }
  /**
   * Lo que despacha el panel de ajustes. Es una acción propia y no dos porque
   * la usuaria pulsó «Aplicar» una vez: deshacer debe revertirlo entero
   * (RF-10c).
   */
  | {
      readonly type: 'applySettings';
      readonly columns: number;
      readonly patternType: PatternType;
    }
  | { readonly type: 'addColor'; readonly color: HexColor }
  | { readonly type: 'addColorRaisingLimit'; readonly color: HexColor }
  | { readonly type: 'updateColor'; readonly index: number; readonly color: HexColor }
  | { readonly type: 'raiseDeclaredColorCount'; readonly declaredCount: number }
  | {
      readonly type: 'removeColor';
      readonly index: number;
      readonly reassignTo: number | null;
    }
  | { readonly type: 'strokeStart'; readonly cells: readonly CellRef[] }
  | { readonly type: 'strokePaint'; readonly cells: readonly CellRef[] }
  | { readonly type: 'strokeEnd' }
  | { readonly type: 'undo' }
  | { readonly type: 'redo' }
  | { readonly type: 'completeRow' }
  | { readonly type: 'goToRow'; readonly row: number }
  | { readonly type: 'resetProgress' }
  | { readonly type: 'undoProgress' }
  | { readonly type: 'redoProgress' }
  | { readonly type: 'setReadingAlternates'; readonly alternates: boolean }
  | {
      readonly type: 'loaded';
      readonly pattern: BraceletPatternDto;
      readonly savedFrom: EntityId | null;
    }
  | { readonly type: 'saved'; readonly savedFrom: EntityId };

export function createSession(
  pattern: BraceletPatternDto,
  savedFrom: EntityId | null,
): PatternSession {
  return {
    pattern,
    past: [],
    future: [],
    tool: { kind: 'color', index: 0 },
    savedFrom,
    dirty: false,
    lastError: null,
    stroke: NO_STROKE,
    progressPast: [],
    progressFuture: [],
  };
}

/**
 * Registra un cambio del avance en su propio historial.
 *
 * No toca `past` ni `future`, y marca `dirty` para que el autoguardado del
 * borrador lo persista: el avance viaja dentro del patrón, así que guardarlo no
 * necesita código propio (RF-53, RF-54).
 */
function commitProgress(session: PatternSession, pattern: BraceletPatternDto): PatternSession {
  if (pattern === session.pattern || pattern.progress === session.pattern.progress) {
    return { ...session, pattern, lastError: null };
  }

  return {
    ...session,
    pattern,
    progressPast: [...session.progressPast, session.pattern.progress],
    progressFuture: [],
    dirty: true,
    lastError: null,
  };
}

/** Deshacer y rehacer el avance son la misma operación en sentidos opuestos. */
function moveProgress(session: PatternSession, direction: 'undo' | 'redo'): PatternSession {
  const from = direction === 'undo' ? session.progressPast : session.progressFuture;
  const previous = from.at(-1);
  if (previous === undefined) return session;

  // Entre que se guardó el valor y ahora, el diseño pudo perder filas: acotar
  // es lo que impide que deshacer viole I8 (data-model §6).
  const restored = clampProgress(session.pattern, previous);
  const current = session.pattern.progress;

  return {
    ...session,
    pattern: { ...session.pattern, progress: restored },
    progressPast:
      direction === 'undo' ? session.progressPast.slice(0, -1) : [...session.progressPast, current],
    progressFuture:
      direction === 'undo'
        ? [...session.progressFuture, current]
        : session.progressFuture.slice(0, -1),
    dirty: true,
    lastError: null,
  };
}

/** Ejecuta una operación de avance capturando sus errores esperables. */
function attemptProgress(
  session: PatternSession,
  operate: () => BraceletPatternDto,
): PatternSession {
  try {
    return commitProgress(session, operate());
  } catch (error) {
    if (!isDomainError(error)) throw error;
    return { ...session, lastError: error };
  }
}

/**
 * Traduce un índice de color de la paleta anterior a la nueva tras quitar
 * `removed`. Es la misma regla que aplica `removeColor` a las celdas: si no se
 * aplicara también a la herramienta activa, quedaría apuntando a otro color.
 */
function shiftTool(tool: PatternTool, removed: number): PatternTool {
  if (tool.kind !== 'color') return tool;
  if (tool.index === removed) return { kind: 'color', index: 0 };
  return { kind: 'color', index: tool.index < removed ? tool.index : tool.index - 1 };
}

/** Incorpora el resultado de una operación al historial. */
function commit(
  session: PatternSession,
  edit: PatternEdit,
  tool: PatternTool = session.tool,
): PatternSession {
  if (isNoChange(edit.undo)) {
    return session.lastError === null ? session : { ...session, lastError: null };
  }

  return {
    ...session,
    pattern: edit.pattern,
    past: [...session.past, edit.undo],
    future: [],
    tool,
    dirty: true,
    lastError: null,
  };
}

/**
 * Incorpora un tramo del trazo en curso.
 *
 * El primer cambio real empuja su propio parche; los siguientes se fusionan en
 * él, de modo que todo el trazo es **una sola** entrada del historial. La
 * fusión es una concatenación y no necesita cuidar el orden: `paintCells` ya
 * descarta las celdas que no cambian, así que cada posición aparece una vez y
 * con su valor anterior al trazo.
 */
function commitStroke(session: PatternSession, edit: PatternEdit): PatternSession {
  if (isNoChange(edit.undo)) {
    return session.lastError === null ? session : { ...session, lastError: null };
  }

  const open = session.stroke.committed ? session.past.at(-1) : undefined;

  const past =
    open?.kind === 'cells' && edit.undo.kind === 'cells'
      ? [
          ...session.past.slice(0, -1),
          { kind: 'cells' as const, cells: [...open.cells, ...edit.undo.cells] },
        ]
      : [...session.past, edit.undo];

  return {
    ...session,
    pattern: edit.pattern,
    past,
    future: [],
    dirty: true,
    lastError: null,
    stroke: { open: true, committed: true },
  };
}

/** Ejecuta una operación del dominio capturando sus errores esperables. */
function attempt(
  session: PatternSession,
  operate: () => PatternEdit,
  nextTool?: (edit: PatternEdit) => PatternTool,
): PatternSession {
  try {
    const edit = operate();
    return commit(session, edit, nextTool?.(edit) ?? session.tool);
  } catch (error) {
    if (!isDomainError(error)) throw error;
    return { ...session, lastError: error };
  }
}

/** Como `attempt`, pero fusionando el resultado en el trazo en curso. */
function attemptStroke(session: PatternSession, operate: () => PatternEdit): PatternSession {
  try {
    return commitStroke(session, operate());
  } catch (error) {
    if (!isDomainError(error)) throw error;
    return { ...session, lastError: error };
  }
}

export function patternSessionReducer(
  session: PatternSession,
  action: PatternAction,
): PatternSession {
  switch (action.type) {
    case 'paint':
      return attempt(session, () =>
        session.tool.kind === 'eraser'
          ? clearCell(session.pattern, action.row, action.column)
          : paintCell(session.pattern, action.row, action.column, session.tool.index),
      );

    case 'selectTool':
      return { ...session, tool: action.tool, lastError: null };

    case 'appendRow':
      return attempt(session, () => appendRow(session.pattern));

    case 'insertRowAbove':
      return attempt(session, () => insertRowAbove(session.pattern, action.row));

    case 'duplicateRow':
      return attempt(session, () => duplicateRow(session.pattern, action.row));

    case 'removeRow':
      return attempt(session, () => removeRow(session.pattern, action.row));

    case 'resizeColumns':
      return attempt(session, () => resizeColumns(session.pattern, action.columns));

    case 'setPatternType':
      return attempt(session, () => setPatternType(session.pattern, action.patternType));

    case 'applySettings':
      return attempt(session, () =>
        applyDesignSettings(session.pattern, {
          columns: action.columns,
          patternType: action.patternType,
        }),
      );

    case 'addColor':
      return attempt(session, () => addColor(session.pattern, action.color));

    case 'addColorRaisingLimit':
      return attempt(session, () => addColorRaisingDeclaredCount(session.pattern, action.color));

    case 'updateColor':
      return attempt(session, () => updateColor(session.pattern, action.index, action.color));

    case 'raiseDeclaredColorCount':
      return attempt(session, () => raiseDeclaredColorCount(session.pattern, action.declaredCount));

    case 'removeColor':
      return attempt(
        session,
        () => removeColor(session.pattern, action.index, action.reassignTo),
        () => shiftTool(session.tool, action.index),
      );

    case 'strokeStart': {
      const started: PatternSession = { ...session, stroke: { open: true, committed: false } };
      return attemptStroke(started, () => paintCells(started.pattern, action.cells, started.tool));
    }

    case 'strokePaint':
      if (!session.stroke.open) return session;
      return attemptStroke(session, () => paintCells(session.pattern, action.cells, session.tool));

    case 'strokeEnd':
      return session.stroke.open ? { ...session, stroke: NO_STROKE } : session;

    case 'undo': {
      const patch = session.past.at(-1);
      if (!patch) return session;

      const { pattern, undo } = applyPatch(session.pattern, patch);
      return {
        ...session,
        pattern,
        past: session.past.slice(0, -1),
        future: [...session.future, undo],
        dirty: true,
        lastError: null,
      };
    }

    case 'redo': {
      const patch = session.future.at(-1);
      if (!patch) return session;

      const { pattern, undo } = applyPatch(session.pattern, patch);
      return {
        ...session,
        pattern,
        past: [...session.past, undo],
        future: session.future.slice(0, -1),
        dirty: true,
        lastError: null,
      };
    }

    case 'completeRow':
      return attemptProgress(session, () => completeRow(session.pattern));

    case 'goToRow':
      return attemptProgress(session, () => goToRow(session.pattern, action.row));

    case 'resetProgress':
      return attemptProgress(session, () => resetProgress(session.pattern));

    case 'undoProgress':
      return moveProgress(session, 'undo');

    case 'redoProgress':
      return moveProgress(session, 'redo');

    case 'setReadingAlternates': {
      // No entra a ningún historial: es una preferencia de lectura del diseño,
      // no un avance ni una edición de la cuadrícula (RF-28e).
      const pattern = setReadingAlternates(session.pattern, action.alternates);
      if (pattern === session.pattern) return session;
      return { ...session, pattern, dirty: true, lastError: null };
    }

    case 'loaded':
      return createSession(action.pattern, action.savedFrom);

    case 'saved':
      return { ...session, savedFrom: action.savedFrom, dirty: false, lastError: null };
  }
}
